/**
 * The monastery page: gallery, record, rituals, etiquette, visiting.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import MonasteryImage from '../components/MonasteryImage';
import PhotoCredit from '../components/PhotoCredit';
import PhotoLightbox from '../components/PhotoLightbox';
import EventModal from '../components/EventModal';
import { CardHeading, EmptyState, Notice } from '../components/ui';
import { CATEGORY_ICONS } from '../components/MonasteryCard';
import VerificationBadge from '../components/VerificationBadge';
import PartnerBadge from '../components/PartnerBadge';
import DirectSupport from '../components/DirectSupport';
import { useBooking } from '../context/BookingContext';
import { api } from '../lib/api';
import { formatAmount, formatEventDateShort } from '../lib/format';
import { trackEvent } from '../lib/consent';

export default function MonasteryDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { isSaved, toggleSaved } = useBooking();
  const [searchParams, setSearchParams] = useSearchParams();
  const [monastery, setMonastery] = useState(null);
  const [error, setError] = useState('');
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxAt, setLightboxAt] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [events, setEvents] = useState([]);
  const [showPast, setShowPast] = useState(false);

  // The same double-pulse the card's heart plays on save — fires once on the
  // false→true transition, never on unsave. Native has no save action on its
  // detail screen to match (only the card carries one there); this keeps the
  // web-only addition consistent with the card's heart rather than inventing
  // a second, unrelated feel for the same gesture.
  const [pulsing, setPulsing] = useState(false);
  const wasSaved = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    setMonastery(null);
    setActivePhoto(0);
    setLightboxAt(null);
    api
      .monastery(id, controller.signal)
      .then(({ monastery: record }) => setMonastery(record))
      .catch((err) => err.name !== 'AbortError' && setError(err.message));
    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    if (!monastery) return;
    trackEvent('view_item', {
      item_id: monastery.id,
      item_name: monastery.name,
      item_category: monastery.traditionLabel,
    });
  }, [monastery?.id]);

  // Events load on their own so the monastery record — the page's critical
  // path — is never held up waiting for them. The section simply appears once
  // they arrive, and renders nothing when there are none.
  useEffect(() => {
    const controller = new AbortController();
    setEvents([]);
    setShowPast(false);
    api
      .monasteryEvents(id, controller.signal)
      .then(({ events: list }) => setEvents(list || []))
      .catch(() => {});
    return () => controller.abort();
  }, [id]);

  const savedNow = monastery ? isSaved(monastery.id) : false;
  useEffect(() => {
    if (savedNow && !wasSaved.current) {
      setPulsing(true);
      const timer = setTimeout(() => setPulsing(false), 1000);
      return () => clearTimeout(timer);
    }
    wasSaved.current = savedNow;
  }, [savedNow]);

  if (error) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('monasteryDetail.notFound', 'We could not find that monastery')}
          action={<Link to="/explore" className="btn btn--primary">{t('monasteryDetail.backToExplore', 'Back to Explore')}</Link>}
        >
          {error}
        </EmptyState>
      </div>
    );
  }

  if (!monastery) {
    return (
      <div className="shell section">
        <div className="skeleton" style={{ height: 320, borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }

  const photos = monastery.photos || [];
  const videos = monastery.videos || [];
  const saved = savedNow;
  const available = monastery.offeringsAvailable !== false;
  const hasOfferings = available && (monastery.services?.length ?? 0) > 0;
  const hasDirectSupport = monastery.donationUrlStatus === 'verified' && !!monastery.donationUrl;
  const teacher = monastery.abbot || monastery.founder;

  const upcomingEvents = events.filter((e) => !e.isPast);
  const pastEvents = events.filter((e) => e.isPast);
  const hasEvents = events.length > 0;
  const shownEvents = showPast ? events : upcomingEvents.length ? upcomingEvents : events;
  const openEventId = searchParams.get('event');
  const openEvent = openEventId ? events.find((e) => e.id === openEventId) : null;
  const openEventModal = (eventId) => {
    const next = new URLSearchParams(searchParams);
    next.set('event', eventId);
    setSearchParams(next);
  };
  const closeEventModal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('event');
    setSearchParams(next, { replace: true });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: monastery.name, url });
      } catch {
        /* user dismissed the share sheet */
      }
      return;
    }
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      // Older/permission-restricted browsers: the execCommand path.
      const field = document.createElement('textarea');
      field.value = url;
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      try { copied = document.execCommand('copy'); } catch { copied = false; }
      field.remove();
    }
    if (copied) {
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2000);
    }
  };

  return (
    <article className="detail">
      {/* Name above the photos on a wide screen (they sit contained there, the
          Airbnb pattern); the photo leads full-bleed on a phone, with the name
          just below. CSS `order` on `.detail` flips the two — see pages.css. */}
      <header className="detail-title shell">
        {monastery.tibetanName && (
          <p className="detail-title__tib tibetan" lang="bo">{monastery.tibetanName}</p>
        )}
        <div className="detail-title__row">
          <h1 className="display display--lg">{monastery.name}</h1>
          <div className="detail-title__actions">
            <button type="button" className="detail-title__action" onClick={handleShare}>
              <Icon name="share" size={15} />
              {shareCopied
                ? t('monasteryDetail.linkCopied', 'Link copied')
                : t('monasteryDetail.share', 'Share')}
            </button>
            <button
              type="button"
              className={`detail-title__action save-toggle${saved ? ' is-saved' : ''}${pulsing ? ' is-pulsing' : ''}`}
              onClick={() => toggleSaved(monastery.id)}
              aria-pressed={saved}
            >
              <Icon name="heart" size={15} solid={saved} />
              {saved ? t('common.saved', 'Following') : t('common.save', 'Follow')}
            </button>
          </div>
        </div>
        {monastery.alsoKnownAs && (
          <p className="muted" style={{ fontSize: 14 }}>
            {t('monasteryDetail.alsoKnownAs', 'Also known as')} {monastery.alsoKnownAs}
          </p>
        )}
        {(monastery.location || teacher) && (
          <p className="detail-title__meta">
            {monastery.location && (
              <span><Icon name="location" size={14} />{monastery.location}</span>
            )}
            {teacher && (
              <span><Icon name="people" size={14} />{teacher}</span>
            )}
          </p>
        )}
      </header>

      <MonasteryHero
        photos={photos}
        activePhoto={activePhoto}
        setActivePhoto={setActivePhoto}
        monastery={monastery}
        onOpenLightbox={(index) => setLightboxAt(index)}
      />

      <div className="shell detail-below">
        {/* Static badges only — what this place *is* (tradition, verification,
            heritage). The one interactive choice, Follow, lives on the hero so
            it never reads as "one more tag in the row". */}
        <div className="row detail-marks" style={{ gap: 'var(--space-sm)' }}>
          <span className="tag">{monastery.traditionLabel}</span>
          {monastery.authorizedPartner && <PartnerBadge />}
          <VerificationBadge
            status={monastery.verificationStatus}
            explain
            claimHref={`/monastery/${monastery.slug || monastery.id}/claim`}
          />
          {monastery.badge && (
            <span className="tag tag--gold">
              <Icon name="lotus" size={12} />
              {monastery.badge}
            </span>
          )}
        </div>

        {/* The jump bar leads with one filled action — the platform's own:
            sponsoring a ritual through GOMPA if this monastery offers that,
            otherwise supporting it directly. The rest stay outline so the eye
            lands on the primary first. Rendered only when there is somewhere
            to jump to beyond contact. */}
        {(hasOfferings || hasDirectSupport || hasEvents) && (
          <nav className="detail-actions" aria-label={t('monasteryDetail.onThisPage', 'On this page')}>
            {hasOfferings && (
              <a href="#offerings" className="detail-actions__cta">
                <Icon name="flame" size={15} />
                {t('monasteryDetail.actionOffer', 'Make an offering')}
              </a>
            )}
            {hasDirectSupport && (
              <a
                href="#support-directly"
                className={hasOfferings ? undefined : 'detail-actions__cta'}
              >
                <Icon name="heart" size={15} />
                {t('monasteryDetail.actionSupport', 'Support directly')}
              </a>
            )}
            {hasEvents && (
              <a href="#events" className={hasOfferings || hasDirectSupport ? undefined : 'detail-actions__cta'}>
                <Icon name="calendar" size={15} />
                {t('monasteryDetail.actionEvents', 'Events')}
              </a>
            )}
            <a href="#contact">
              <Icon name="mail" size={15} />
              {t('monasteryDetail.actionContact', 'Visit / contact')}
            </a>
          </nav>
        )}

        <div className="detail-layout" style={{ paddingBlock: '0 var(--space-5xl)' }}>
          <div className="stack stack--lg">

            {videos.length > 0 && (
              <div>
                <p className="eyebrow" style={{ marginBottom: 'var(--space-sm)' }}>
                  {t('monasteryDetail.watchTitle', 'Watch')}
                </p>
                <div className="monastery-videos">
                  {videos.map((video, index) => (
                    <figure key={index} className="monastery-video">
                      <video
                        controls
                        preload="metadata"
                        playsInline
                        poster={video.poster || undefined}
                        src={video.source}
                      />
                      {video.credit && (
                        <figcaption className="subtle">
                          {t('monasteryDetail.videoCredit', { credit: video.credit, defaultValue: `Video: ${video.credit}` })}
                          {video.license ? ` · ${video.license}` : ''}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {/* Descriptions are plain text. Honour any blank-line paragraph
                breaks the monastery gave us, and if it handed us one long
                block, break it near the middle at a sentence boundary so it
                skims as prose rather than a wall. The "About" eyebrow gives
                the block the same small heading every other section has. */}
            {monastery.description && (
              <div className="detail-about">
                <p className="eyebrow" style={{ marginBottom: 'var(--space-sm)' }}>
                  {t('monasteryDetail.aboutTitle', 'About')}
                </p>
                {splitDescription(monastery.description).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
                {monastery.hasExtendedAbout && (
                  <Link
                    to={`/monastery/${monastery.slug || monastery.id}/about`}
                    className="detail-about__more"
                  >
                    {t('monasteryDetail.readMore', 'Read more')}
                    <Icon name="arrowRight" size={15} />
                  </Link>
                )}
              </div>
            )}

            {!available && (
              <Notice tone="warn" icon="info" title={t('monasteryDetail.offeringsUnavailable', "Offerings aren't open here yet")}>
                {monastery.unavailableReason}
              </Notice>
            )}

            {hasEvents && (
              <section id="events" className="m-events scroll-anchor">
                <div className="m-events__head">
                  <p className="eyebrow">{t('monasteryDetail.eventsTitle', 'Events')}</p>
                  {pastEvents.length > 0 && upcomingEvents.length > 0 && (
                    <button
                      type="button"
                      className="m-events__toggle"
                      onClick={() => setShowPast((v) => !v)}
                    >
                      {showPast
                        ? t('events.hidePast', 'Hide past events')
                        : t('events.showPast', { count: pastEvents.length, defaultValue: `Show past events (${pastEvents.length})` })}
                    </button>
                  )}
                </div>

                {upcomingEvents.length === 0 && !showPast && (
                  <p className="subtle" style={{ marginBottom: 'var(--space-md)' }}>
                    {t('events.noUpcoming', 'No upcoming events right now.')}
                  </p>
                )}

                <ul className="m-events__row">
                  {shownEvents.map((event) => (
                    <li key={event.id}>
                      <button
                        type="button"
                        className="event-card"
                        onClick={() => {
                          trackEvent('select_item', {
                            item_id: event.id, item_name: event.title, item_list_name: 'monastery_events',
                          });
                          openEventModal(event.id);
                        }}
                      >
                        <span className={`event-card__media${event.imageUrl ? '' : ' event-card__media--empty'}`}>
                          {event.imageUrl
                            ? <img src={event.imageUrl} alt="" loading="lazy" />
                            : <Icon name="lotus" size={28} />}
                          <span className="event-card__date">
                            {formatEventDateShort(event.startDate, event.endDate)}
                          </span>
                          {event.isPast && (
                            <span className="event-card__past">{t('events.pastLabel', 'Past event')}</span>
                          )}
                        </span>
                        <span className="event-card__title">{event.title}</span>
                        {event.summary && <span className="event-card__summary">{event.summary}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* One eyebrow over both cards below, present only when at least
                one applies. Naming them as a pair — "Ways to give" — is what
                actually keeps two bordered cards from reading as two random
                boxes stacked by accident; the visual distinction between them
                (dashed vs. gold-accented) still does the job of telling them
                apart, this just tells the reader up front that the grouping
                itself is deliberate. */}
            {((monastery.donationUrlStatus === 'verified' && monastery.donationUrl) ||
              (available && monastery.services?.length > 0)) && (
              <div className="stack">
                <p className="eyebrow">{t('monasteryDetail.waysToGiveTitle', 'Ways to give')}</p>

                {/* ----------------------------------------------- direct support -- */}
                {/* Defense in depth: the server already withholds `donationUrl`
                    unless `donationUrlStatus` is `verified` (see repositories/
                    partners.js#bySlugOrId), but this re-checks the status here
                    too rather than trusting the URL's mere presence — a
                    suspended or pending destination must never render as a live
                    CTA, per the spec's donation-safety rule. */}
                {monastery.donationUrlStatus === 'verified' && monastery.donationUrl && (
                  <div id="support-directly" className="scroll-anchor">
                    <DirectSupport
                      monasteryName={monastery.name}
                      monasteryId={monastery.id}
                      donationUrl={monastery.donationUrl}
                      verified={monastery.donationUrlStatus === 'verified'}
                      verifiedAt={monastery.donationUrlVerifiedAt}
                    />
                  </div>
                )}

                {/* --------------------------------------------------------- rituals -- */}
                {/* Distinct on purpose from the Direct Support card above: a
                    gold-accented border (this is a GOMPA-facilitated flow, the
                    same visual family as the aside's "Sponsor an offering" CTA)
                    and its own microcopy naming GOMPA as the coordinator, so the
                    two never read as one undifferentiated list of ways to give
                    money — that conflation is exactly what the spec's trust rules
                    warn against. */}
                {available && monastery.services?.length > 0 && (
                  <section id="offerings" className="card card__pad scroll-anchor" style={{ borderColor: 'var(--accent)' }}>
                    <CardHeading icon="flame" title={t('monasteryDetail.offeringsTitle', 'Offerings you can sponsor')} />
                    <p className="subtle" style={{ marginBottom: 'var(--space-md)' }}>
                      {t('monasteryDetail.offeringsCoordinatedNote', {
                        name: monastery.name,
                        defaultValue: `GOMPA coordinates this offering with ${monastery.name}.`,
                      })}
                    </p>
                    <div className="stack">
                      {/* Each option carries its own real price now
                          (`option.amountCents`, set independently per option) —
                          this is the fix for the documented flat-pricing bug
                          ("3-Day Retreat" costing the same as "Single Day"), so
                          a service with more than one price shows "from" its
                          lowest rather than one number standing in for all of
                          them. Actual option selection happens on the Offer page
                          this links to, not here. */}
                      {monastery.services.map((service) => {
                        const amounts = (service.options ?? []).map((o) => o.amountCents);
                        const fromCents = amounts.length ? Math.min(...amounts) : null;
                        const varies = new Set(amounts).size > 1;
                        const allPerUnit = service.options?.length > 0 && service.options.every((o) => o.isPerUnit);
                        return (
                          <Link
                            key={service.id}
                            className="service"
                            to={`/monastery/${monastery.slug || monastery.id}/offer/${service.id}`}
                            onClick={() => trackEvent('select_item', {
                              item_id: service.id, item_name: service.name, item_list_name: 'monastery_detail',
                            })}
                          >
                            <span className="service__plate">
                              <Icon name={CATEGORY_ICONS[service.category] || 'lotus'} size={21} />
                            </span>
                            <span>
                              <span className="service__name">{service.name}</span>
                              <span className="service__desc" style={{ display: 'block' }}>
                                {service.description}
                              </span>
                            </span>
                            <span className="service__price">
                              <b>
                                {varies && `${t('monasteryDetail.priceFrom', 'From')} `}
                                {formatAmount(fromCents)}
                              </b>
                              <span>
                                {allPerUnit
                                  ? t('monasteryDetail.perLamp', 'per lamp')
                                  : t('monasteryDetail.contribution', 'contribution')}
                              </span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                    <p className="subtle" style={{ marginTop: 'var(--space-lg)' }}>
                      {t('monasteryDetail.monasteryDirectNote', {
                        name: monastery.name,
                        defaultValue: `Each contribution goes to ${monastery.name} for the ritual you choose. GOMPA validates listings and coordinates delivery.`,
                      })}
                    </p>
                  </section>
                )}
              </div>
            )}

          </div>

          {/* -------------------------------------------------------- aside -- */}
          <aside className="detail-aside">
            {/* One quiet panel, not two boxes — record and contact are both
                "facts about this place," and a hairline divider reads
                calmer here than a second bordered, shadowed card directly
                underneath the first. */}
            <section className="card card--flat card__pad">
              <CardHeading icon="journal" title={t('monasteryDetail.recordTitle', 'The record')} />
              <dl className="fact-list">
                <Fact icon="time" label={t('monasteryDetail.facts.established', 'Established')} value={monastery.established} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                <Fact icon="lotus" label={t('monasteryDetail.facts.tradition', 'Tradition')} value={monastery.traditionLabel} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                <Fact icon="people" label={t('monasteryDetail.facts.founder', 'Founder')} value={monastery.founder} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                <Fact icon="people" label={t('monasteryDetail.facts.abbot', 'Abbot')} value={monastery.abbot} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                <Fact icon="location" label={t('monasteryDetail.facts.location', 'Location')} value={monastery.location} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                <Fact icon="time" label={t('monasteryDetail.facts.visitingHours', 'Visiting hours')} value={monastery.visitingHours} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
              </dl>

              <div id="contact" className="scroll-anchor" style={{ marginTop: 'var(--space-xl)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--border-light)' }}>
                <p className="eyebrow" style={{ marginBottom: 'var(--space-md)' }}>
                  {t('monasteryDetail.contactTitle', 'Contact')}
                </p>
                {monastery.contact ? (
                  <dl className="fact-list">
                    {monastery.contact.phone && (
                      <Fact icon="phone" label={t('monasteryDetail.contact.phone', 'Phone')} value={monastery.contact.phone} href={`tel:${monastery.contact.phone}`} onClick={() => trackEvent('contact_monastery', { item_id: monastery.id, channel: 'phone' })} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                    )}
                    {monastery.contact.email && (
                      <Fact icon="mail" label={t('monasteryDetail.contact.email', 'Email')} value={monastery.contact.email} href={`mailto:${monastery.contact.email}`} onClick={() => trackEvent('contact_monastery', { item_id: monastery.id, channel: 'email' })} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                    )}
                    {monastery.contact.website && (
                      <Fact icon="globe" label={t('monasteryDetail.contact.website', 'Website')} value={monastery.contact.website.replace(/^https?:\/\//, '')} href={monastery.contact.website} external onClick={() => trackEvent('contact_monastery', { item_id: monastery.id, channel: 'website' })} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                    )}
                    {monastery.contact.address && (
                      <Fact icon="location" label={t('monasteryDetail.contact.address', 'Address')} value={monastery.contact.address} notPublishedText={t('monasteryDetail.facts.notPublished', 'Not published')} />
                    )}
                  </dl>
                ) : (
                  <p className="subtle">
                    {t('monasteryDetail.contact.unverifiedNotice', 'We have not verified contact details for this monastery. Write to gompaapp@gmail.com if you represent this monastery.')}
                  </p>
                )}
              </div>
            </section>

            {available && monastery.services?.length > 0 && (
              <section className="card card__pad" style={{ background: 'var(--primary)', borderColor: 'var(--accent)' }}>
                <p className="eyebrow eyebrow--on-dark">{t('monasteryDetail.sponsorCardTitle', 'Sponsor an offering')}</p>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13.5, margin: 'var(--space-sm) 0 var(--space-lg)' }}>
                  {t('monasteryDetail.sponsorCardSub', 'Your dedication is recited as you wrote it.')}
                </p>
                <Link
                  to={`/monastery/${monastery.slug || monastery.id}/offer/${monastery.services[0].id}`}
                  className="btn btn--gold btn--block"
                  onClick={() => trackEvent('cta_click', { cta_id: 'sponsor_card' })}
                >
                  <Icon name="flame" size={17} solid />
                  {t('monasteryDetail.beginOffering', 'Begin an offering')}
                </Link>
              </section>
            )}
          </aside>
        </div>
      </div>

      {lightboxAt != null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          index={lightboxAt}
          setIndex={setLightboxAt}
          onClose={() => setLightboxAt(null)}
          label={monastery.name}
        />
      )}

      {openEvent && (
        <EventModal
          event={openEvent}
          monasteryName={monastery.name}
          monasteryId={monastery.id}
          onClose={closeEventModal}
        />
      )}
    </article>
  );
}

function Fact({ icon, label, value, href, external, onClick, notPublishedText = 'Not published' }) {
  const missing = !value;
  return (
    <div className={`fact${missing ? ' fact--unknown' : ''}`}>
      <Icon name={icon} size={17} />
      <div>
        <dt>{label}</dt>
        <dd>
          {missing ? (
            notPublishedText
          ) : href ? (
            <a href={href} onClick={onClick} {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}>
              {value}
              {external && <Icon name="external" size={13} style={{ marginLeft: 4, verticalAlign: -2 }} />}
            </a>
          ) : (
            value
          )}
        </dd>
      </div>
    </div>
  );
}

/**
 * The page hero. On a phone: one photograph filling the width, swipe or tap
 * to open the full gallery. On a wide screen: a contained mosaic of up to
 * five tiles (the Airbnb pattern), any tile opening the gallery. A placeholder
 * fills the same frame when the monastery has no photography yet. The back and
 * follow controls float over the top corners on every size.
 */
const GALLERY_DOT_LIMIT = 5;
const HERO_TILES = 5;
const WIDE_QUERY = '(min-width: 940px)';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Honour blank-line paragraph breaks; failing that, split one long block near
 * its midpoint at a sentence boundary so the About text skims as prose.
 */
function splitDescription(text) {
  const paras = String(text)
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (paras.length !== 1 || paras[0].length < 240) return paras;

  const sentences = paras[0].match(/[^.!?]+[.!?]+["'”’)]*\s*/g);
  if (!sentences || sentences.length < 2) return paras;

  const half = paras[0].length / 2;
  let run = 0;
  let cut = 0;
  for (let i = 0; i < sentences.length - 1; i += 1) {
    run += sentences[i].length;
    if (run >= half) { cut = i + 1; break; }
  }
  if (!cut) cut = sentences.length - 1;

  const first = sentences.slice(0, cut).join('').trim();
  const second = sentences.slice(cut).join('').trim();
  // Don't strand a fragment — if either half is tiny, leave it whole.
  if (first.length < 60 || second.length < 60) return paras;
  return [first, second];
}

function MonasteryHero({
  photos,
  activePhoto,
  setActivePhoto,
  monastery,
  onOpenLightbox,
}) {
  const { t } = useTranslation();
  const isWide = useMediaQuery(WIDE_QUERY);
  const hasPhotos = photos.length > 0;
  const hasMultiple = photos.length > 1;
  const touchStart = useRef(null);

  const goTo = (delta) =>
    setActivePhoto((current) => (current + delta + photos.length) % photos.length);

  const onTouchStart = (event) => {
    const point = event.touches[0];
    touchStart.current = point ? { x: point.clientX, y: point.clientY } : null;
  };
  const onTouchEnd = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const end = event.changedTouches[0];
    if (!end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    // A horizontal swipe steps the carousel. A plain tap falls through to the
    // frame button's click, which opens the full gallery.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      goTo(dx < 0 ? 1 : -1);
    }
  };

  // The mosaic shows 1, 2, 3, or 5 tiles — never 4 (a lone wide strip under
  // the pair reads worse than "big + 2", with the 4th behind "Show all").
  const tileCount = photos.length === 4 ? 3 : Math.min(photos.length, HERO_TILES);
  const tiles = photos.slice(0, tileCount);

  // Only the phone hero (photo at the very top of the page) carries a floating
  // back control; on a wide screen the name and the site nav sit above it.
  const backControl = (
    <div className="detail-hero__controls" onClick={(event) => event.stopPropagation()}>
      <Link
        to="/explore"
        className="detail-hero__btn"
        aria-label={t('monasteryDetail.allMonasteries', 'All monasteries')}
      >
        <Icon name="arrowLeft" size={19} />
      </Link>
    </div>
  );

  const showAllButton = hasMultiple ? (
    <button
      type="button"
      className="detail-hero__all"
      onClick={(event) => { event.stopPropagation(); onOpenLightbox(isWide ? 0 : activePhoto); }}
    >
      <Icon name="menu" size={15} />
      {t('monasteryDetail.showAllPhotos', {
        count: photos.length,
        defaultValue: `Show all ${photos.length} photos`,
      })}
    </button>
  ) : null;

  if (isWide && hasPhotos) {
    return (
      <div className="detail-hero detail-hero--grid">
        <div className={`detail-hero__grid detail-hero__grid--n${tiles.length}`}>
          {tiles.map((tilePhoto, index) => (
            <button
              key={index}
              type="button"
              className="detail-hero__tile"
              onClick={() => onOpenLightbox(index)}
              aria-label={t('monasteryDetail.openPhoto', {
                n: index + 1,
                total: photos.length,
                defaultValue: `Open photograph ${index + 1} of ${photos.length}`,
              })}
            >
              <img src={tilePhoto.source} alt="" loading="eager" fetchPriority={index === 0 ? 'high' : 'auto'} decoding="async" />
            </button>
          ))}
          {showAllButton}
        </div>

        <div className="detail-hero__credits">
          {photos.map((creditPhoto, index) =>
            creditPhoto.credit ? <PhotoCredit key={index} photo={creditPhoto} /> : null
          )}
        </div>
      </div>
    );
  }

  const photo = photos[activePhoto];
  const openGallery = hasPhotos ? () => onOpenLightbox(activePhoto) : undefined;
  const stop = (fn) => (event) => { event.stopPropagation(); fn(); };

  return (
    <div className="detail-hero">
      <div
        className={`detail-hero__frame${hasPhotos ? ' detail-hero--tappable' : ''}`}
        onClick={openGallery}
        onKeyDown={openGallery ? (e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openGallery(); } } : undefined}
        role={openGallery ? 'button' : undefined}
        tabIndex={openGallery ? 0 : undefined}
        aria-label={openGallery ? t('monasteryDetail.openGallery', 'Open the photo gallery') : undefined}
        onTouchStart={hasMultiple ? onTouchStart : undefined}
        onTouchEnd={hasMultiple ? onTouchEnd : undefined}
      >
        <MonasteryImage
          photo={photo}
          monastery={monastery}
          variant="hero"
          className="detail-hero__img"
        />

        {backControl}

        {hasMultiple && (
          <>
            <button
              type="button"
              className="detail-hero__arrow detail-hero__arrow--prev"
              onClick={stop(() => goTo(-1))}
              aria-label="Previous photograph"
            >
              <Icon name="chevronLeft" size={20} />
            </button>
            <button
              type="button"
              className="detail-hero__arrow detail-hero__arrow--next"
              onClick={stop(() => goTo(1))}
              aria-label="Next photograph"
            >
              <Icon name="chevronRight" size={20} />
            </button>
            {photos.length <= GALLERY_DOT_LIMIT ? (
              <div className="detail-hero__dots" role="tablist" aria-label="Choose a photograph">
                {photos.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={index === activePhoto}
                    aria-label={`Photograph ${index + 1}`}
                    className={`detail-hero__dot${index === activePhoto ? ' is-active' : ''}`}
                    onClick={stop(() => setActivePhoto(index))}
                  />
                ))}
              </div>
            ) : (
              <span className="detail-hero__counter">
                {activePhoto + 1} / {photos.length}
              </span>
            )}
            {showAllButton}
          </>
        )}
      </div>

      {photo?.credit && (
        <div className="detail-hero__credits">
          <PhotoCredit photo={photo} />
        </div>
      )}
    </div>
  );
}
